import { cx } from '@emotion/css';
import { useCss, inset, cloneElementWithCss } from '@nettbureau/styling';
import { Children, isValidElement } from 'react';
import { createComponent } from '../system/factories.js';
import '@emotion/react/jsx-runtime';
import '@react-aria/utils';
import 'ramda';
import 'react-aria';
import { useColorMode, useColorInverted } from '../theming/theme-provider.js';

/**
 * A map of tint options for each fill color alias and interaction state.
 */ const tints = {
    none: {
        hovered: {
            effect: 'darken',
            tone: 'weaker'
        },
        pressed: {
            effect: 'darken',
            tone: 'weak'
        }
    },
    soft: {
        hovered: {
            effect: 'darken',
            tone: 'weaker'
        },
        pressed: {
            effect: 'darken',
            tone: 'weak'
        }
    },
    medium: {
        hovered: {
            effect: 'lighten',
            tone: 'weak'
        },
        pressed: {
            effect: 'lighten',
            tone: 'weak'
        }
    },
    bold: {
        hovered: {
            effect: 'lighten',
            tone: 'weak'
        },
        pressed: {
            effect: 'darken',
            tone: 'weak'
        }
    }
};
/**
 * A map of tint options for each fill color alias and interaction state for dark mode.
 */ const darkTints = {
    none: {
        hovered: {
            effect: 'lighten',
            tone: 'weak'
        },
        pressed: {
            effect: 'lighten',
            tone: 'medium'
        }
    },
    soft: {
        hovered: {
            effect: 'lighten',
            tone: 'weak'
        },
        pressed: {
            effect: 'lighten',
            tone: 'medium'
        }
    },
    medium: {
        hovered: {
            effect: 'lighten',
            tone: 'medium'
        },
        pressed: {
            effect: 'lighten',
            tone: 'strong'
        }
    },
    bold: {
        hovered: {
            effect: 'lighten',
            tone: 'weak'
        },
        pressed: {
            effect: 'lighten',
            tone: 'medium'
        }
    }
};
/**
 * The interactive overlay component is responsible for dynamically adding a tint to the wrapped
 * element based on user interactions.
 */ const InteractiveTint = createComponent((props)=>{
    const isDarkMode = useColorMode() === 'dark';
    const isInverted = useColorInverted();
    const isDark = isInverted || isDarkMode;
    const { children, disabled = false, className, hovered, hoverTint, pressed, pressTint, ...otherProps } = props;
    const css = useCss(({ mixins })=>!disabled && {
            '&::before': [
                {
                    borderRadius: 'inherit',
                    content: '""',
                    position: 'absolute'
                },
                inset(0),
                mixins.transition({
                    properties: [
                        'background'
                    ]
                }),
                hovered && hoverTint && mixins.tint(hoverTint),
                pressed && pressTint && mixins.tint(pressTint)
            ],
            ...Object.entries(isDark ? darkTints : tints).reduce((styles, [tone, { hovered: hoveredTint, pressed: pressedTint }])=>({
                    ...styles,
                    [`&[data-fill-tone="${tone}"]::before`]: [
                        hovered && !hoverTint && mixins.tint(hoveredTint),
                        pressed && !pressTint && mixins.tint(pressedTint)
                    ]
                }), {})
        });
    const child = Children.only(children);
    return /*#__PURE__*/ isValidElement(child) ? cloneElementWithCss(child, {
        ...otherProps,
        'data-interactive': disabled ? undefined : true,
        className: child.props.className ? cx(child.props.className, className) : className,
        css
    }) : null;
});

export { InteractiveTint };
//# sourceMappingURL=interactive-tint.js.map
